import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spinner, Table, Alert } from 'react-bootstrap';
import {
  useListEntrepreneurApplicationsQuery,
  useApproveEntrepreneurFirmMutation,
  useRejectEntrepreneurFirmMutation,
  useVerifyEntrepreneurDomainMutation,
  useListEntrepreneurProvisionQueueQuery,
  useRejectEntrepreneurProvisionMutation,
  useWithdrawEntrepreneurClaudeSeatMutation,
  useGetEntrepreneurClaudeCertUrlMutation,
} from '../../app/api';
import {
  brand, usd, cardStyle, headStyle, EntPage, runAction, ClaudeTrackBadge, claudeSignedUp,
  ActionItem, ActionsCard, Fact,
} from './common';
import ActivationChargeModal from './ActivationChargeModal';
import { FirmBillingPanel } from './billing';

/* One firm's CLAUDE page (Entrepreneur -> Claude -> Claude Firms -> a firm).
 *
 * Only the optional Claude add-on lives here: the application, the domain,
 * seat requests (confirm in the Anthropic console, charge the activation) and
 * Stripe billing. The firm's hub - its members and their logins - is on the
 * Firms page (FirmDetail), and the two link to each other. Requesting a seat
 * for a particular person happens there, on the member's row. Firm admins set
 * members' spend limits themselves (Spend Limits API), so there's no
 * limit-request queue here. */

const memberName = (m) => [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
const isReady = (q) => q.claude_certified && q.firm_billing_ready;
const dateOnly = (v) => (v ? new Date(v).toLocaleDateString() : '-');
const PENDING = ['REQUESTED', 'APPROVED'];
// How a closed request ended, for the "Completed" list.
const CLOSED_LABEL = {
  INVITED: { text: 'Added to Claude', color: '#1a7f4e', bg: 'rgba(31,138,84,0.12)', icon: 'check-circle-fill' },
  ACTIVE: { text: 'Added to Claude', color: '#1a7f4e', bg: 'rgba(31,138,84,0.12)', icon: 'check-circle-fill' },
  REJECTED: { text: 'Rejected', color: '#b42318', bg: 'rgba(180,35,24,0.10)', icon: 'x-circle-fill' },
};

export default function ClaudeFirmDetail() {
  const { firmId: firmIdParam } = useParams();
  const firmId = Number(firmIdParam);
  const navigate = useNavigate();

  const { data: apps = [], isLoading, refetch: refetchApps } = useListEntrepreneurApplicationsQuery();
  // isError is destructured deliberately: without it a 500 or a 403 renders as
  // "No open seat requests", which reassures the viewer that there is no work
  // to do. That exact confusion hid a broken endpoint for weeks.
  // include_closed: completed requests (added to Claude / rejected) come back
  // too, so staff can see who has already been set up. `queue` stays the open
  // requests only - everything that counts outstanding work reads it.
  const {
    data: allRequests = [], refetch: refetchQueue,
    isError: queueError, error: queueErrorInfo,
  } = useListEntrepreneurProvisionQueueQuery({ firm_id: firmId, include_closed: true });
  const queue = useMemo(() => allRequests.filter(q => PENDING.includes(q.provision_status)), [allRequests]);
  const completed = useMemo(
    () => allRequests
      .filter(q => !PENDING.includes(q.provision_status))
      .sort((a, b) => String(b.closed_at || '').localeCompare(String(a.closed_at || ''))),
    [allRequests],
  );

  const [approveFirm] = useApproveEntrepreneurFirmMutation();
  const [rejectFirm] = useRejectEntrepreneurFirmMutation();
  const [verifyDomain] = useVerifyEntrepreneurDomainMutation();
  const [rejectProvision] = useRejectEntrepreneurProvisionMutation();
  const [withdrawSeat] = useWithdrawEntrepreneurClaudeSeatMutation();
  const [getCertUrl] = useGetEntrepreneurClaudeCertUrlMutation();
  const [activationTarget, setActivationTarget] = useState(null);

  const app = useMemo(() => apps.find(a => a.firm_id === firmId), [apps, firmId]);
  const refetchAll = async () => { await Promise.all([refetchApps(), refetchQueue()]); };

  const viewCertificate = async (uuid) => {
    try {
      const res = await getCertUrl({ uuid }).unwrap();
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch { window.alert('Could not open that certificate.'); }
  };

  const headerActions = (
    <>
      <Button variant="outline-light" size="sm" onClick={() => navigate(`/entrepreneur/firms/${firmId}`)}>
        <i className="bi bi-people me-1" />Firm &amp; members
      </Button>
      <Button variant="outline-light" size="sm" onClick={() => navigate('/entrepreneur/claude-firms')}>
        <i className="bi bi-arrow-left me-1" />All Claude firms
      </Button>
    </>
  );

  if (isLoading) {
    return <EntPage title="Claude" actions={headerActions}><div className="text-center p-5"><Spinner /></div></EntPage>;
  }
  if (!app) {
    return (
      <EntPage title="Firm not found" actions={headerActions}>
        <Card style={cardStyle}><Card.Body style={{ color: brand.charcoal }}>This firm doesn&apos;t exist or has been removed.</Card.Body></Card>
      </EntPage>
    );
  }

  const signedUp = claudeSignedUp(app);
  const hasCard = !!app.payment_authorized;
  const readyMembers = queue.filter(isReady);
  const notReadyMembers = queue.filter(q => !isReady(q));

  // ── Outstanding Claude actions, in setup order ──
  const actions = [];
  // Surfaced here, not just in the seat card: this list is the part people
  // actually read, and a silent load failure looked identical to "nothing to do".
  if (queueError) {
    actions.push(
      <ActionItem key="queue-error" icon="exclamation-octagon" title="Seat queue could not be loaded"
        detail={queueErrorInfo?.status === 403
          ? 'Your account lacks permission to view provisioning, so any waiting members are hidden.'
          : 'The provisioning list failed to load, so members waiting for Claude access may not be shown below. This is a system error, not an empty queue.'}>
        <Button size="sm" variant="outline-danger" onClick={refetchQueue}>Try again</Button>
      </ActionItem>,
    );
  }
  if (app.application_status === 'SUBMITTED') {
    actions.push(
      <ActionItem key="review" icon="clipboard-check" title="Review the firm's Claude application"
        detail="The firm has applied for Claude. Approve to move them forward, or reject.">
        <Button size="sm" variant="success" onClick={() => runAction(approveFirm, { firm_id: firmId }, refetchAll)}>Approve</Button>
        <Button size="sm" variant="outline-danger" onClick={() => runAction(rejectFirm, { firm_id: firmId }, refetchAll)}>Reject</Button>
      </ActionItem>,
    );
  }
  if (app.domain_status !== 'VERIFIED' && (app.email_domains?.length || 0) > 0 && !['REJECTED', 'DRAFT'].includes(app.application_status)) {
    actions.push(
      <ActionItem key="domain" icon="globe" title="Confirm the firm's domain is added to Claude"
        detail={`Whitelist ${app.email_domains.join(', ')} in the Anthropic console, then mark it verified here.`}>
        <Button size="sm" variant="outline-primary" onClick={() => runAction(verifyDomain, { firm_id: firmId }, refetchAll)}>Mark domain verified</Button>
      </ActionItem>,
    );
  }
  if (!hasCard && !['DRAFT', 'REJECTED'].includes(app.application_status)) {
    actions.push(
      <ActionItem key="card" icon="credit-card" title="Add a payment method"
        detail="The firm has no card on file - seats can't be charged until one is added. Add one in Billing below.">
      </ActionItem>,
    );
  }
  if (readyMembers.length > 0) {
    actions.push(
      <ActionItem key="provision" icon="person-check" title={`${readyMembers.length} member${readyMembers.length === 1 ? '' : 's'} ready to add to Claude`}
        detail="Each has finished training and the firm can be billed. Add them in the Anthropic console with their requested limit, then confirm and enter the license cost below." />,
    );
  }
  // Certified but not provisionable - almost always the firm's payment method.
  // Called out separately because the member has done everything asked of them.
  const certifiedBlocked = notReadyMembers.filter(q => q.claude_certified);
  if (certifiedBlocked.length > 0) {
    actions.push(
      <ActionItem key="certified-blocked" icon="hourglass-split"
        title={`${certifiedBlocked.length} certified member${certifiedBlocked.length === 1 ? ' is' : 's are'} waiting on the firm`}
        detail={hasCard
          ? 'They have finished training but cannot be provisioned yet. Check the seat list for what is outstanding.'
          : 'They have finished training and cannot be provisioned until this firm has a payment method on file, because approving a seat raises a charge.'} />,
    );
  }

  return (
    <EntPage
      title={app.firm_name}
      subtitle={`Claude · ${app.claude_seats ?? 0} seat${app.claude_seats === 1 ? '' : 's'} in Claude${queue.length ? ` · ${queue.length} requested` : ''}`}
      actions={headerActions}
      icon="stars"
    >
      <ActionsCard actions={actions} doneText={signedUp ? 'Nothing outstanding on this firm’s Claude account.' : 'Nothing to do until the firm applies for Claude.'} />

      <Card style={{ ...cardStyle, marginBottom: '1.25rem' }}>
        <Card.Header style={headStyle}>Application</Card.Header>
        <Card.Body>
          <ClaudeApplication app={app} signedUp={signedUp}
            onApprove={() => runAction(approveFirm, { firm_id: firmId }, refetchAll)}
            onReject={() => runAction(rejectFirm, { firm_id: firmId }, refetchAll)}
            onVerifyDomain={() => runAction(verifyDomain, { firm_id: firmId }, refetchAll)} />
        </Card.Body>
      </Card>

      <Card style={{ ...cardStyle, marginBottom: '1.25rem' }}>
        <Card.Header style={headStyle}>Seat requests</Card.Header>
        <Card.Body>
          <p style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: 0 }}>
            Members whose firm admin (or PCA) asked for a Claude seat, with requests already added to Claude or rejected listed under Completed. To request one for someone else, use their row on the{' '}
            <Button variant="link" size="sm" onClick={() => navigate(`/entrepreneur/firms/${firmId}`)} style={{ padding: 0, fontSize: '0.8rem', verticalAlign: 'baseline' }}>members page</Button>.
          </p>
        {queueError ? (
          <Alert variant="danger" className="mb-0 py-2" style={{ fontSize: '0.85rem' }}>
            <div style={{ fontWeight: 700 }}>
              <i className="bi bi-exclamation-octagon-fill me-1" />
              Couldn&apos;t load the seat queue
            </div>
            <div style={{ marginTop: 4 }}>
              {queueErrorInfo?.status === 403
                ? 'Your account does not have permission to view provisioning.'
                : `The server returned an error${queueErrorInfo?.status ? ` (${queueErrorInfo.status})` : ''}. This is not the same as having no seats to provision - there may be members waiting.`}
            </div>
            <Button size="sm" variant="outline-danger" className="mt-2" onClick={refetchQueue}>Try again</Button>
          </Alert>
        ) : queue.length === 0 ? (
          <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>No open seat requests.</div>
        ) : (
          <Table responsive className="mb-0" style={{ fontSize: '0.85rem' }}>
            <thead><tr style={{ color: brand.charcoal }}>
              <th style={{ padding: '0.5rem 0.75rem' }}>Member</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>Claude cert</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }} title="Monthly spend limit to set for this seat in the Anthropic console">Requested limit</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }} />
            </tr></thead>
            <tbody>
              {[...readyMembers, ...notReadyMembers].map(q => (
                <tr key={q.uuid}>
                  <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                    <div style={{ fontWeight: 600, color: brand.deepBlue }}>{memberName(q)}</div>
                    <div style={{ color: brand.charcoal, fontSize: '0.75rem' }}>{q.email}</div>
                    {!q.firm_billing_ready && (
                      <div style={{ color: brand.goldenrod, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <i className="bi bi-exclamation-triangle-fill me-1" />No payment method
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                    <CertStatus q={q} onView={viewCertificate} />
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 700, color: brand.deepBlue }}>{usd(q.limit_cents)}<span style={{ color: brand.charcoal, fontWeight: 500 }}>/mo</span></span>
                    {!q.limit_is_override && (
                      <div style={{ color: brand.charcoal, fontSize: '0.68rem' }}>firm default</div>
                    )}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Button
                      size="sm" variant="success" className="me-1"
                      disabled={!q.claude_certified}
                      onClick={() => setActivationTarget(q)}
                      title={!q.claude_certified ? 'Member must complete Claude certification first' : !q.firm_billing_ready ? 'No card on file - you can confirm without a charge' : 'Enter Claude’s prorated charge (or confirm with no charge)'}
                    >
                      Confirm added to Claude
                    </Button>
                    <Button size="sm" variant="outline-secondary" className="me-1" title="Back to hub only - the request was opened by mistake or the firm changed its mind"
                      onClick={() => { if (window.confirm(`Withdraw the seat request for ${memberName(q)}? They stay in the hub.`)) runAction(withdrawSeat, { uuid: q.uuid }, refetchAll); }}>
                      Withdraw
                    </Button>
                    <Button size="sm" variant="outline-danger" title="Record that PCA turned this request down" onClick={() => runAction(rejectProvision, { uuid: q.uuid }, refetchAll)}>Reject</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!queueError && completed.length > 0 && (
          <>
            <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem', margin: '1.25rem 0 0.25rem' }}>
              Completed <span style={{ color: brand.charcoal, fontWeight: 500 }}>({completed.length})</span>
            </div>
            <Table responsive className="mb-0" style={{ fontSize: '0.85rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={{ padding: '0.5rem 0.75rem' }}>Member</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Claude cert</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Outcome</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }} title="When PCA confirmed or rejected the request">Date</th>
              </tr></thead>
              <tbody>
                {completed.map(q => {
                  const label = CLOSED_LABEL[q.provision_status] || { text: q.provision_status, color: brand.charcoal, bg: 'rgba(10,41,71,0.06)', icon: 'dash-circle' };
                  return (
                    <tr key={q.uuid}>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 600, color: brand.deepBlue }}>{memberName(q)}</div>
                        <div style={{ color: brand.charcoal, fontSize: '0.75rem' }}>{q.email}</div>
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                        <CertStatus q={q} onView={viewCertificate} />
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                        <span style={{ background: label.bg, color: label.color, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          <i className={`bi bi-${label.icon} me-1`} />{label.text}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap', color: brand.charcoal }}>
                        {dateOnly(q.closed_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Header style={headStyle}>Billing &amp; invoices</Card.Header>
        <Card.Body>
          {!signedUp ? (
            <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>Billing becomes available once the firm applies for Claude.</div>
          ) : (
            <FirmBillingPanel firmId={firmId} firmName={app.firm_name} onChanged={refetchApps} />
          )}
        </Card.Body>
      </Card>

      {activationTarget && (
        <ActivationChargeModal
          member={activationTarget}
          onClose={() => setActivationTarget(null)}
          onCharged={async () => { setActivationTarget(null); await refetchAll(); }}
        />
      )}
    </EntPage>
  );
}

// Certification badge + "View" link for a seat request row (open or completed).
function CertStatus({ q, onView }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      {q.claude_certified ? (
        <span style={{ background: 'rgba(31,138,84,0.12)', color: '#1a7f4e', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          <i className="bi bi-patch-check-fill me-1" />Certified
        </span>
      ) : (
        <span style={{ background: 'rgba(185,119,14,0.12)', color: '#b9770e', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          Not yet certified
        </span>
      )}
      {q.has_certificate && (
        <Button size="sm" variant="outline-secondary" onClick={() => onView(q.uuid)} title="Open the uploaded certificate" style={{ padding: '0.1rem 0.5rem', fontSize: '0.72rem' }}>
          <i className="bi bi-file-earmark-check me-1" />View
        </Button>
      )}
    </div>
  );
}
CertStatus.propTypes = {
  q: PropTypes.object.isRequired,
  onView: PropTypes.func.isRequired,
};

// The firm's Claude application: where it stands and the facts staff need to
// configure the firm's Claude Enterprise group. A firm that never applied gets
// a plain explanation instead of a wall of blank fields.
function ClaudeApplication({ app, signedUp, onApprove, onReject, onVerifyDomain }) {
  if (!signedUp) {
    return (
      <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start', background: 'rgba(10,41,71,0.04)', borderRadius: 10, padding: '0.9rem 1rem' }}>
        <i className="bi bi-info-circle" style={{ color: brand.tealBlue, fontSize: '1.2rem' }} />
        <div style={{ fontSize: '0.85rem', color: brand.charcoal }}>
          <div style={{ fontWeight: 700, color: brand.deepBlue }}>This firm hasn&apos;t applied for Claude</div>
          Its members still use the hub. If the firm wants Claude, a firm admin completes
          <em> Claude Set Up</em> in their portal (domains, limits, card), and the application shows up here for review.
          Any seat you request for them from the Members page waits below until then.
        </div>
      </div>
    );
  }
  const hasCard = !!app.payment_authorized;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <span style={{ fontWeight: 700, color: brand.deepBlue }}>Application</span>
        <ClaudeTrackBadge status={app.application_status} />
        {app.application_status === 'SUBMITTED' && (
          <>
            <Button size="sm" variant="success" onClick={onApprove}>Approve</Button>
            <Button size="sm" variant="outline-danger" onClick={onReject}>Reject</Button>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <Fact label="Domains" value={(app.email_domains || []).join(', ') || '-'} />
        <Fact label="Domain" value={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {app.domain_status}
            {app.domain_status !== 'VERIFIED' && (app.email_domains?.length || 0) > 0 && app.application_status !== 'REJECTED' && (
              <Button size="sm" variant="link" onClick={onVerifyDomain} style={{ padding: 0, fontSize: '0.75rem' }}>Mark verified</Button>
            )}
          </span>
        )} />
        {/* The two Claude Enterprise group limits the firm chose at sign-up, so
            staff can configure the firm's group + per-user defaults to match. */}
        <Fact label="User default limit" value={app.default_user_limit_cents != null ? `${usd(app.default_user_limit_cents)}/mo` : '-'} />
        <Fact label="Group limit" value={app.group_limit_cents != null ? `${usd(app.group_limit_cents)}/mo` : 'Not set'} />
        <Fact label="Billing contact" value={app.billing_contact_email || '-'} />
        <Fact label="Payment method" value={hasCard ? 'On file' : 'None'} />
        <Fact label="Applied" value={dateOnly(app.applied_at)} />
        <Fact label="Approved" value={dateOnly(app.approved_at)} />
      </div>
    </div>
  );
}
ClaudeApplication.propTypes = {
  app: PropTypes.object.isRequired,
  signedUp: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onVerifyDomain: PropTypes.func.isRequired,
};
